/**
 * The wallboard's shared account-ack state (US 34815). Acks are FlexData rows
 * on the bridge (entity `wallboard`, category `account-ack`, key = account),
 * read with the seat JWT every Roost client already holds, so the Control
 * Center and the iPad see the same snooze. A click applies locally at once;
 * other screens pick it up on their next load or 5 min reconcile. If the
 * bridge write fails, the ack still holds on this screen until it reloads,
 * and `error` says so until a write succeeds (Task 34832).
 */
import { computed, signal } from '@angular/core';
import type { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  ACCOUNT_ACK_FLEX,
  type AccountAcks,
  acknowledgeAccount,
  observeAcks,
  parseAccountAcks,
  suppressAcknowledged,
} from './account-ack.logic';
import type { Alert } from './attention.logic';

export const ACK_GAP_KEY = 'Account acks';
export const ACK_WRITE_FAILED =
  'saved on this screen only, the bridge write failed';
export const ACK_READ_FAILED = 'acks from other screens could not be read';

export class AccountAckSync {
  readonly acks = signal<AccountAcks>(new Map());
  /** A failed write (the snooze is local only) outlives later good reads. */
  readonly error = computed(() => this.writeError() ?? this.readError());
  private readonly readError = signal<string | null>(null);
  private readonly writeError = signal<string | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly baseUrl: () => string
  ) {}

  /** The alerts to show: account alerts for snoozed accounts dropped. */
  visible(alerts: readonly Alert[], nowMs: number): Alert[] {
    return suppressAcknowledged(alerts, this.acks(), nowMs);
  }

  /** Source errors plus this sync's error, for the gaps lines. */
  withError(
    errors: Readonly<Partial<Record<string, string>>>
  ): Readonly<Partial<Record<string, string>>> {
    const e = this.error();
    return e ? { ...errors, [ACK_GAP_KEY]: e } : errors;
  }

  async load(nowMs = Date.now()): Promise<void> {
    const q = `entityType=${ACCOUNT_ACK_FLEX.entityType}&category=${ACCOUNT_ACK_FLEX.category}`;
    try {
      const rows = await firstValueFrom(
        this.http.get<Record<string, unknown>[]>(
          `${this.baseUrl()}/ryn-db/flex-data?${q}`
        )
      );
      const remote = parseAccountAcks(Array.isArray(rows) ? rows : []);
      this.acks.set(observeAcks(this.acks(), remote, nowMs));
      this.readError.set(null);
    } catch {
      this.readError.set(ACK_READ_FAILED);
    }
  }

  async acknowledge(accountUpn: string, atMs = Date.now()): Promise<void> {
    this.acks.set(acknowledgeAccount(this.acks(), accountUpn, atMs));
    try {
      await firstValueFrom(
        this.http.post(`${this.baseUrl()}/ryn-db/flex-data`, {
          ...ACCOUNT_ACK_FLEX,
          key: accountUpn.toLowerCase(),
          jsonPayload: { ackedAt: new Date(atMs).toISOString() },
        })
      );
      this.writeError.set(null);
    } catch {
      this.writeError.set(ACK_WRITE_FAILED);
    }
  }
}
